<div class="min-h-screen bg-neutral-50">
	<div class="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 md:py-8">
		<!-- Header -->
		<div class="mb-4 md:mb-8">
			<h1 class="text-xl md:text-3xl font-cenzo font-bold text-charcoal mb-1 md:mb-2">Site Settings</h1>
			<p class="text-charcoal/60 text-sm md:text-base">Manage your website content across all pages</p>
		</div>

		<!-- Success/Error Messages -->
		@if (saveMessage()) {
		<div class="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-center gap-2">
			<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
			</svg>
			{{ saveMessage() }}
		</div>
		}

		@if (errorMessage()) {
		<div class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-center gap-2">
			<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
			</svg>
			{{ errorMessage() }}
		</div>
		}

		<!-- Loading State -->
		@if (isLoading()) {
		<div class="flex justify-center py-20">
			<div class="w-8 h-8 border-2 border-charcoal/20 border-t-charcoal rounded-full animate-spin"></div>
		</div>
		} @else {

		<!-- Page Tabs -->
		<div class="mb-4 md:mb-6">
			<div class="border-b border-charcoal/10 overflow-x-auto">
				<nav class="flex gap-1 min-w-max" aria-label="Tabs">
					@for (tab of pageTabs; track tab.id) {
					<button
						(click)="selectTab(tab.id)"
						[class.bg-white]="activeTab() === tab.id"
						[class.border-primary]="activeTab() === tab.id"
						[class.text-primary]="activeTab() === tab.id"
						[class.border-transparent]="activeTab() !== tab.id"
						[class.text-charcoal/60]="activeTab() !== tab.id"
						class="px-3 md:px-6 py-2 md:py-3 text-xs md:text-sm font-semibold border-b-2 -mb-px rounded-t-lg transition-all hover:text-primary flex items-center gap-1.5 md:gap-2 whitespace-nowrap">
						<svg class="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							@switch (tab.id) {
								@case ('homepage') {
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
								}
								@case ('services') {
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
								}
								@case ('about') {
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
								}
								@case ('seo') {
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
								}
								@case ('general') {
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
								}
								@case ('email') {
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
								}
								@case ('others') {
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/>
								}
							}
						</svg>
						<span class="hidden sm:inline">{{ tab.label }}</span>
						<span class="sm:hidden">{{ tab.label.split(' ')[0] }}</span>
					</button>
					}
				</nav>
			</div>
		</div>

		<!-- Save & Publish Button (Top) -->
		<div class="mb-4 md:mb-6 flex justify-end">
			<button
				(click)="saveAndPublish()"
				[disabled]="isSaving() || isPublishing()"
				class="inline-flex items-center gap-2 px-4 py-2 md:px-8 md:py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-sm md:text-base">
				@if (isSaving() || isPublishing()) {
					<svg class="w-4 h-4 md:w-5 md:h-5 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					<span>{{ isSaving() ? 'Saving...' : 'Publishing...' }}</span>
				} @else {
					<svg class="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
					</svg>
					<span class="hidden sm:inline">Save & Publish</span>
					<span class="sm:hidden">Save</span>
				}
			</button>
		</div>

		<!-- Tab Content -->
		<div class="bg-white rounded-xl shadow-sm border border-charcoal/10 overflow-hidden">
			<!-- Tab Header -->
			<div class="px-4 md:px-6 py-3 md:py-5 border-b border-charcoal/10 bg-gradient-to-r from-primary/5 to-transparent">
				<h2 class="text-base md:text-xl font-bold text-charcoal">{{ getActiveTabLabel() }} Settings</h2>
				<p class="text-xs md:text-sm text-charcoal/60 mt-0.5 md:mt-1">Edit the content displayed on the {{ getActiveTabLabel().toLowerCase() }}</p>
			</div>

			<!-- Settings List -->
			<div class="p-4 md:p-6">
				@for (group of getFilteredGroups(); track group.section) {
				<div class="mb-6 md:mb-8 last:mb-0">
					<!-- Section Header -->
					<div class="flex flex-wrap items-center gap-2 md:gap-3 mb-4 md:mb-6 pb-2 md:pb-3 border-b border-charcoal/10">
						<div class="w-1 h-5 md:h-6 bg-primary rounded-full"></div>
						<h3 class="text-base md:text-lg font-semibold text-charcoal">{{ getSectionTitle(group.section) }}</h3>
						<span class="text-xs text-charcoal/40 bg-charcoal/5 px-2 py-0.5 md:py-1 rounded-full">{{ group.settings.length }} fields</span>
					</div>

					<!-- Settings Grid -->
					<div class="grid gap-4 md:gap-6">
						@for (setting of group.settings; track setting.id) {
						<div class="group">
							<div class="flex flex-col gap-1 md:gap-1.5 mb-2">
								<label class="text-xs md:text-sm font-semibold text-charcoal">{{ setting.label }}</label>
								@if (setting.description) {
								<p class="text-xs text-charcoal/50">{{ setting.description }}</p>
								}
							</div>

							<!-- Toggle Switch (for boolean settings) -->
							@if (isToggleSetting(setting)) {
							<div class="flex items-center gap-3">
								<label class="relative inline-flex items-center cursor-pointer">
									<input type="checkbox" [checked]="setting.value === '1'" (change)="setting.value = setting.value === '1' ? '0' : '1'" class="sr-only peer"/>
									<div class="w-11 h-6 bg-charcoal/20 peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
								</label>
								<span class="text-sm text-charcoal/70">{{ setting.value === '1' ? 'Yes' : 'No' }}</span>
							</div>
							}

							<!-- Text Input -->
							@if ((setting.type === 'text' || setting.type === 'url' || setting.type === 'number') && !isToggleSetting(setting)) {
							<input
								[(ngModel)]="setting.value"
								[type]="setting.type === 'number' ? 'number' : 'text'"
								class="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-charcoal/20 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
								placeholder="Enter {{ setting.label }}"
							/>
							}

							<!-- Textarea -->
							@if (setting.type === 'textarea') {
							<textarea
								[(ngModel)]="setting.value"
								rows="4"
								class="w-full px-4 py-3 border border-charcoal/20 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-y transition-all"
								placeholder="Enter {{ setting.label }}"
							></textarea>
							}

							<!-- Image Upload -->
							@if (setting.type === 'image') {
							<div class="space-y-3">
								@if (setting.value && !hasFileSelected(setting)) {
								<div class="relative w-full max-w-sm">
									<img
										[src]="getMediaPreview(setting)"
										[alt]="setting.label"
										class="w-full h-40 object-cover rounded-lg border border-charcoal/10"
									/>
								</div>
								}
								<label class="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-charcoal/20 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
									<input
										type="file"
										accept="image/*"
										(change)="onFileSelected($event, setting)"
										class="hidden"
									/>
									<svg class="w-5 h-5 text-charcoal/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
									</svg>
									<span class="text-sm text-charcoal/60">
										{{ hasFileSelected(setting) ? '✓ ' + getFileName(setting) : 'Choose image file' }}
									</span>
								</label>
							</div>
							}
						</div>
						}
					</div>
				</div>
				}

				@if (getFilteredGroups().length === 0) {
				<div class="text-center py-12 text-charcoal/50">
					<svg class="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
					</svg>
					<p>No settings found for this page</p>
				</div>
				}
			</div>
		</div>

		<!-- Team Members Management (About Us tab only) -->
		@if (activeTab() === 'about') {
		<div class="mt-6 bg-white rounded-xl shadow-sm border border-charcoal/10 overflow-hidden">
			<div class="px-4 md:px-6 py-3 md:py-5 border-b border-charcoal/10 bg-gradient-to-r from-primary/5 to-transparent flex items-center justify-between">
				<div>
					<h2 class="text-base md:text-xl font-bold text-charcoal">Team Members</h2>
					<p class="text-xs md:text-sm text-charcoal/60 mt-0.5 md:mt-1">Manage team members displayed on the About Us page</p>
				</div>
				<button (click)="openAddMember()"
					class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
					</svg>
					Add Member
				</button>
			</div>

			<div class="p-4 md:p-6">
				@if (teamMembers().length === 0) {
				<div class="text-center py-12 text-charcoal/50">
					<svg class="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
					</svg>
					<p class="font-medium mb-1">No team members yet</p>
					<p class="text-sm">Add your team members to showcase them on the About Us page</p>
				</div>
				} @else {
				<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					@for (member of teamMembers(); track member.id) {
					<div class="border border-charcoal/10 rounded-xl overflow-hidden group hover:shadow-lg transition-all" [class.opacity-50]="!member.is_active">
						<!-- Member Image -->
						<div class="aspect-square bg-charcoal/5 relative overflow-hidden">
							@if (member.image_url) {
							<img [src]="member.image_url" [alt]="member.name" class="w-full h-full object-cover"/>
							} @else {
							<div class="w-full h-full flex items-center justify-center">
								<svg class="w-16 h-16 text-charcoal/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
								</svg>
							</div>
							}
							<!-- Overlay Actions -->
							<div class="absolute inset-0 bg-black/50 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
								<button (click)="openEditMember(member)"
									class="p-2 bg-white rounded-full text-charcoal hover:bg-primary hover:text-white transition-colors" title="Edit">
									<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
									</svg>
								</button>
								<button (click)="toggleMemberActive(member)"
									class="p-2 bg-white rounded-full text-charcoal hover:bg-yellow-500 hover:text-white transition-colors"
									[title]="member.is_active ? 'Deactivate' : 'Activate'">
									<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										@if (member.is_active) {
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/>
										} @else {
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
										}
									</svg>
								</button>
								<button (click)="deleteMember(member)"
									class="p-2 bg-white rounded-full text-charcoal hover:bg-red-500 hover:text-white transition-colors" title="Delete">
									<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
									</svg>
								</button>
							</div>
						</div>
						<!-- Member Info -->
						<div class="p-4">
							<h4 class="font-bold text-charcoal text-sm">{{ member.name }}</h4>
							<p class="text-xs text-primary/70 font-medium">{{ member.title }}</p>
							@if (member.description) {
							<p class="text-xs text-charcoal/50 mt-1 line-clamp-2">{{ member.description }}</p>
							}
							@if (!member.is_active) {
							<span class="inline-block mt-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Inactive</span>
							}
						</div>
					</div>
					}
				</div>
				}
			</div>
		</div>
		}

<!-- Team Member Modal Template -->
<ng-template #teamMemberModalTemplate>
	<div class="bg-white rounded-2xl shadow-2xl w-full">
		<!-- Header -->
		<div class="sticky top-0 bg-white border-b border-charcoal/10 px-6 py-5 rounded-t-2xl flex items-center justify-between">
			<h3 class="text-xl font-bold text-primary">{{ editingMember() ? 'Edit' : 'Add' }} Team Member</h3>
			<button (click)="closeTeamMemberModal()" class="p-2 hover:bg-charcoal/5 rounded-lg transition-colors">
				<svg class="w-5 h-5 text-charcoal/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
				</svg>
			</button>
		</div>

		<!-- Body -->
		<div class="px-6 py-5 space-y-5">
			<!-- Image Upload -->
			<div class="flex flex-col items-center gap-3">
				<div class="w-32 h-32 rounded-full overflow-hidden bg-charcoal/5 border-2 border-dashed border-charcoal/20 relative group cursor-pointer"
					(click)="memberImageInput.click()">
					@if (memberImagePreview()) {
					<img [src]="memberImagePreview()" alt="Preview" class="w-full h-full object-cover"/>
					} @else {
					<div class="w-full h-full flex flex-col items-center justify-center text-charcoal/30">
						<svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
						</svg>
						<span class="text-xs mt-1">Photo</span>
					</div>
					}
					<div class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
						<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
						</svg>
					</div>
				</div>
				<input #memberImageInput type="file" accept="image/*" class="hidden" (change)="onMemberImageSelected($event)"/>
			</div>

			<!-- Name -->
			<div>
				<label class="block text-sm font-semibold text-charcoal mb-1.5">Name <span class="text-red-500">*</span></label>
				<input [(ngModel)]="memberForm.name" type="text" placeholder="e.g. John Doe"
					class="w-full px-4 py-2.5 border border-charcoal/20 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 text-sm"/>
			</div>

			<!-- Title -->
			<div>
				<label class="block text-sm font-semibold text-charcoal mb-1.5">Title <span class="text-red-500">*</span></label>
				<input [(ngModel)]="memberForm.title" type="text" placeholder="e.g. Senior Hair Stylist"
					class="w-full px-4 py-2.5 border border-charcoal/20 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 text-sm"/>
			</div>

			<!-- Description -->
			<div>
				<label class="block text-sm font-semibold text-charcoal mb-1.5">Description</label>
				<textarea [(ngModel)]="memberForm.description" rows="3" placeholder="Short bio or description..."
					class="w-full px-4 py-2.5 border border-charcoal/20 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 text-sm resize-none"></textarea>
			</div>

			<!-- Active Toggle -->
			<div class="flex items-center gap-3">
				<label class="relative inline-flex items-center cursor-pointer">
					<input type="checkbox" [(ngModel)]="memberForm.is_active" class="sr-only peer"/>
					<div class="w-11 h-6 bg-charcoal/20 peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
				</label>
				<span class="text-sm font-medium text-charcoal">Active</span>
			</div>
		</div>

		<!-- Footer -->
		<div class="px-6 py-4 border-t border-charcoal/10 flex items-center justify-end gap-3">
			<button (click)="closeTeamMemberModal()"
				class="px-5 py-2.5 text-sm font-semibold text-charcoal bg-charcoal/5 rounded-lg hover:bg-charcoal/10 transition-colors">
				Cancel
			</button>
			<button (click)="saveMember()" [disabled]="isSavingMember()"
				class="px-5 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50">
				@if (isSavingMember()) {
				<span class="flex items-center gap-2">
					<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
					</svg>
					Saving...
				</span>
				} @else {
				{{ editingMember() ? 'Update' : 'Add' }} Member
				}
			</button>
		</div>
	</div>
</ng-template>

		<!-- Save & Publish Button (Bottom) -->
		<div class="mt-4 md:mt-6 flex justify-end">
			<button
				(click)="saveAndPublish()"
				[disabled]="isSaving() || isPublishing()"
				class="inline-flex items-center gap-2 px-4 py-2 md:px-8 md:py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-sm md:text-base">
				@if (isSaving() || isPublishing()) {
					<svg class="w-4 h-4 md:w-5 md:h-5 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					<span>{{ isSaving() ? 'Saving...' : 'Publishing...' }}</span>
				} @else {
					<svg class="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
					</svg>
					<span class="hidden sm:inline">Save & Publish</span>
					<span class="sm:hidden">Save</span>
				}
			</button>
		</div>

		}
	</div>
</div>
